import { config } from '../../config'
import { useFlags } from 'launchdarkly-react-client-sdk'

type FeatureFlagName =
  | 'ACFU_PROGRESSIVE_DATE_DISCLOSURE'
  | 'ALLOW_CHANGING_TEAM_ACCESS'
  | 'BLOCK_IN_TRIAL_AFTERCARE_FOLLOW_UP_SCHEDULING'
  | 'BLOCK_OUT_OF_TRIAL_FOLLOW_UP_SCHEDULING'
  | 'CABIN_EMPLOYEE_COMBINED_BOOKING'
  | 'DISPLAY_TECH_SETUP_REASONS'
  | 'ENABLE_AFTERCARE_FOLLOW_UP_RECOMMENDATIONS'
  | 'ENABLE_APPOINTMENT_TYPE_SELECTION'
  | 'ENABLE_AVAILABILITY_CACHE'
  | 'ENABLE_EXTENDED_FOLLOW_UP_RECOMMENDATIONS'
  | 'ENABLE_FINAL_APPOINTMENT_RECOMMENDATIONS'
  | 'ENABLE_HEARING_AID_EVALUATION_RECOMMENDATIONS'
  | 'ENABLE_RESERVATION_WITH_PROVIDER_RECOMMENDATION'
  | 'ENABLE_SHOP_DELIVERY_MODEL_CONFIGURATION'
  | 'ENABLE_SHOP_SCHEDULE_MANAGEMENT'
  | 'ENABLE_SQUAD_BASED_SCHEDULING'
  | 'ENABLE_VIDEO_CARE_CALL_RECOMMENDATIONS'
  | 'POOLED_SCHEDULING'
  | 'PREVENT_DOUBLE_BOOKING_HEARING_AID_EVALUATIONS'
  | 'RECOMMENDATION_BASED_AVAILABILITIES'
  | 'RESERVATION_AVAILABILITY_START_DATE_DAYS_OFFSET'
  | 'RESERVATION_AVAILABILITY_WINDOW_IN_DAYS'
  | 'RESTRICT_CONSULTANT_APPOINTMENT_TYPES_HAE_ONLY'
  | 'SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS'
  | 'SHOW_MANUALLY_ADD_TIME_BUTTON'
  | 'SKIP_48_BUSINESS_HOURS_ON_NEW_HEARING_AID_EVALUATIONS'
  | 'SKIP_48_BUSINESS_HOURS_ON_RESERVATIONS'
  | 'SCHEDULING_V2'
  | 'USE_PRS_FOLLOW_UP_RULE'

type Uuid = string
type FeatureFlagValue = boolean | number | undefined

// Feature flag key values can be found here: https://app.launchdarkly.com/ta-team-america/production/features
const featureFlagNameToValueMap: Record<FeatureFlagName, Uuid> = {
  ACFU_PROGRESSIVE_DATE_DISCLOSURE: 'acfu-progressive-date-disclosure',
  BLOCK_IN_TRIAL_AFTERCARE_FOLLOW_UP_SCHEDULING: 'block-in-trial-aftercare-follow-up-scheduling',
  BLOCK_OUT_OF_TRIAL_FOLLOW_UP_SCHEDULING: 'block-out-of-trial-follow-up-scheduling',
  CABIN_EMPLOYEE_COMBINED_BOOKING: '82b22c46-75fb-49ac-9414-39546ebf7e99',
  DISPLAY_TECH_SETUP_REASONS: 'c7d3d300-8a83-4e31-8395-023cf2bbd1ad',
  ENABLE_AFTERCARE_FOLLOW_UP_RECOMMENDATIONS: '5e15fed3-db3f-42fc-94d0-f7067c7c04c4',
  ENABLE_APPOINTMENT_TYPE_SELECTION: '6eaf932b-37d1-4edd-aa63-04a102036ef5',
  ENABLE_AVAILABILITY_CACHE: 'enable-availability-day-cache',
  ENABLE_EXTENDED_FOLLOW_UP_RECOMMENDATIONS: 'd7f2c17a-76de-4068-a366-72f4d2e97a42',
  ENABLE_FINAL_APPOINTMENT_RECOMMENDATIONS: '6af669e1-105b-4196-b0ab-a23b7cd5902d',
  ENABLE_HEARING_AID_EVALUATION_RECOMMENDATIONS: '0f438a91-a22c-4f37-b62f-44881852ab98',
  ENABLE_RESERVATION_WITH_PROVIDER_RECOMMENDATION: '4921db5d-438e-47a0-9d77-ab0983e4121a',
  ENABLE_SHOP_DELIVERY_MODEL_CONFIGURATION: 'enable-shop-delivery-model-configuration',
  ENABLE_SHOP_SCHEDULE_MANAGEMENT: 'bb25176f-e9e5-430f-8a0f-036b02f19e37',
  ENABLE_VIDEO_CARE_CALL_RECOMMENDATIONS: 'd2976f7a-eda3-41ac-aa19-3ca65849013e',
  POOLED_SCHEDULING: '3f9c2a71-5d4e-4b8a-9c16-7e2f0a4d8b53',
  PREVENT_DOUBLE_BOOKING_HEARING_AID_EVALUATIONS: '8ba5c7f4-1fcf-4b67-9cc6-bfa7f59ccdac',
  RECOMMENDATION_BASED_AVAILABILITIES: 'd60979a9-a290-4669-8532-8a656f61c7fd',
  RESERVATION_AVAILABILITY_START_DATE_DAYS_OFFSET: 'aa70da25-092b-48a0-a8e5-533b5c7040d9',
  RESERVATION_AVAILABILITY_WINDOW_IN_DAYS: '1bd1391d-a966-4da8-b2e4-31f0ba71b002',
  RESTRICT_CONSULTANT_APPOINTMENT_TYPES_HAE_ONLY: '3b36514f-0af9-46af-b8fb-ef79405f0b8a',
  SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS: '060f5629-f781-4afc-afd4-361b204eab8c',
  SCHEDULING_V2: 'scheduling-v2',
  SHOW_MANUALLY_ADD_TIME_BUTTON: '6b261eb7-1d2b-4f09-8063-f343af637560',
  SKIP_48_BUSINESS_HOURS_ON_NEW_HEARING_AID_EVALUATIONS: '3dd9506c-c429-496d-b5b7-1198f90671e7',
  SKIP_48_BUSINESS_HOURS_ON_RESERVATIONS: '757c5b1d-6c0d-457a-9429-14379321e534',
  USE_PRS_FOLLOW_UP_RULE: '32071314-34fc-4ab0-ba6c-19ee042f2d43',
  ENABLE_SQUAD_BASED_SCHEDULING: 'enable-squad-based-scheduling',
  ALLOW_CHANGING_TEAM_ACCESS: 'allow-changing-team-access',
} as const

const defalutFlagValues: Partial<Record<FeatureFlagName, string | number>> = {
  SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS: 62,
}

const featureFlagOverrides = config.FORCE_ENABLED_FEATURE_FLAGS as { name: FeatureFlagName; value: boolean | number }[]
const isLiveEnvironment = config.IS_LIVE_ENVIRONMENT

type FeatureFlag = keyof typeof featureFlagNameToValueMap
type NonEmptyArray<T> = [T, ...T[]]

const overrides = isLiveEnvironment ? [] : featureFlagOverrides

if (overrides.length) {
  console.warn(`⚠️ WARNING ⚠️: The following feature flags are enabled via local environment config`, overrides)
}

const forceEnabledFlags = new Map(overrides.map(({ name, value }) => [name, value]))

/**
 * @param flags The list of feature flags to fetch values for
 * @returns An ordered array of boolean values matching the provided flags array
 */
const useFeatureFlags = <T extends FeatureFlagValue>(flags: NonEmptyArray<FeatureFlag>): T[] => {
  const featureFlags = useFlags()

  return flags.map((flag) => forceEnabledFlags.get(flag) ?? (featureFlags[featureFlagNameToValueMap[flag]] || defalutFlagValues[flag]))
}

export type { FeatureFlagName }
export { featureFlagNameToValueMap, useFeatureFlags }
